export function capitalizeFirst(input: string): string {
  if (!input) return '';
  return input.charAt(0).toUpperCase() + input.slice(1);
}

export function formatFullDateBase(d: Date): string {
  const parts = new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).formatToParts(d);
  const day = parts.find(p => p.type === 'day')?.value ?? '';
  const month = capitalizeFirst(parts.find(p => p.type === 'month')?.value ?? '');
  const year = parts.find(p => p.type === 'year')?.value ?? '';
  return `${day} ${month}, ${year}`;
}

export function formatTodayFull(d: Date): string {
  return `Сегодня, ${formatFullDateBase(d)}`;
}

export function formatNonTodayFull(d: Date): string {
  return formatFullDateBase(d);
}

export function formatMonth(d: Date | null): string {
  if (!d) return '';
  const monthLower = d.toLocaleString('ru-RU', {month: 'long'});
  const month = capitalizeFirst(monthLower);
  const year = d.getFullYear();
  return `${month} ${year}`;
}

export function formatDateSmart(d: Date, today: Date): string {
  return d.getTime() === today.getTime() ? formatTodayFull(d) : formatNonTodayFull(d);
}

export function toISODateString(d: Date): string {
  const year = d.getFullYear();
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function addMonths(d: Date, count: number): Date {
  const res = new Date(d.getFullYear(), d.getMonth() + count, 1);
  res.setHours(0, 0, 0, 0);
  return res;
}

export function formatDateDMY(d: Date): string {
  const day = String(d.getDate());
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}
export function parseISODateStrict(iso: string): Date | null {
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const d = new Date(year, month - 1, day);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function getMonthStart(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function getMonthEnd(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function compareISODateAsc(a: string, b: string): number {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

export type SnapshotRef = {id: number; dateISO: string};

export function findFirstIdAfterOrEqual(
  snapshotsAsc: SnapshotRef[],
  targetISO: string,
): number | null {
  if (!snapshotsAsc || snapshotsAsc.length === 0) return null;
  const found = snapshotsAsc.find(s => s.dateISO >= targetISO);
  return found ? found.id : snapshotsAsc[snapshotsAsc.length - 1].id;
}

export function findPlanIdExactOrAfter(
  snapshotsAsc: SnapshotRef[],
  targetISO: string,
  strictExact: boolean,
): number | null {
  if (!snapshotsAsc || snapshotsAsc.length === 0) return null;
  const exact = snapshotsAsc.find(s => s.dateISO === targetISO);
  if (strictExact && exact) return exact.id;
  if (strictExact && !exact) {
    const after = snapshotsAsc.find(s => s.dateISO >= targetISO);
    return after ? after.id : snapshotsAsc[snapshotsAsc.length - 1].id;
  }
  const afterOrEqual = snapshotsAsc.find(s => s.dateISO >= targetISO);
  return afterOrEqual ? afterOrEqual.id : snapshotsAsc[snapshotsAsc.length - 1].id;
}

export function formatLastUpdate(isoString: string): string {
  if (!isoString) return '-';

  const date = new Date(isoString);
  const now = new Date();

  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  const time = date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  });

  const formatter = new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const dateStr = formatter
    .format(date)
    .replace(' г.', '')
    .replace(/(\d+\s)([A-Za-zА-Яа-яЁё])/, (_, d, m) => d + m.toUpperCase())
    .replace(/([A-Za-zА-Яа-яЁё]+)\s(\d{4})/, '$1, $2');

  return isToday ? `${time}, сегодня, ${dateStr}` : `${time}, ${dateStr}`;
}
